import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseURL } from "./api";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Grid,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

const states = [
  "Maharashtra",
  "Gujarat",
  "Delhi",
  "Karnataka",
  "Tamil Nadu",
  "West Bengal",
  "Rajasthan",
];

const SystemConfiguration = () => {
  const [tabValue, setTabValue] = useState(0);
  const [form, setForm] = useState({
    sgst: "",
    cgst: "",
    igst: "",
    state: "",
  });

  const [allGSTs, setAllGSTs] = useState([]); // Store all GST configs
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  // Fetch all GST configs on component mount
  useEffect(() => {
    fetchAllGSTs();
  }, []);

  const fetchAllGSTs = async () => {
  try {
    const response = await axios.get(`${baseURL}/gst`);
    if (response.data) {
      setAllGSTs(response.data);

      // If at least one config exists, load it into the form by default
      if (response.data.length > 0) {
        setForm({
          sgst: response.data[0].sgst || "",
          cgst: response.data[0].cgst || "",
          igst: response.data[0].igst || "",
          state: response.data[0].state || "",
        });
      }
    }
  } catch (err) {
    console.error(err);
    setMessage("Unable to fetch GST configurations");
    setSeverity("error");
  }
};


  const handleChange = (e) => {
  const { name, value } = e.target;
  setForm({ ...form, [name]: value });

  if (name === "state") {
    const selectedConfig = allGSTs.find((gst) => gst.state === value);
    if (selectedConfig) {
      setForm({
        sgst: selectedConfig.sgst || "",
        cgst: selectedConfig.cgst || "",
        igst: selectedConfig.igst || "",
        state: selectedConfig.state || value,
      });
    }
  }
};


  const handleSave = async () => {
    if (!form.state || form.sgst === "" || form.cgst === "" || form.igst === "") {
      setMessage("Please fill all fields");
      setSeverity("error");
      return;
    }

    try {
      // Try to save directly
      const response = await axios.post(`${baseURL}/gst`, form);
      setMessage("GST configuration saved successfully!");
      setSeverity("success");
      fetchAllGSTs(); // Refresh list
    } catch (err) {
      if (err.response && err.response.data?.message?.includes("already exists")) {
        const confirmOverwrite = window.confirm(
          `GST configuration for ${form.state} already exists. Do you want to overwrite it?`
        );
        if (!confirmOverwrite) return;

        try {
          const updateResponse = await axios.put(`${baseURL}/gst/${form.state}`, form);
          setMessage("GST configuration updated successfully!");
          setSeverity("success");
          fetchAllGSTs(); // Refresh list
        } catch (updateErr) {
          console.error(updateErr);
          setMessage("Error updating GST configuration");
          setSeverity("error");
        }
      } else {
        console.error(err);
        setMessage("Server not reachable or error occurred");
        setSeverity("error");
      }
    }
  };

  const handleReset = () => {
    setForm({ sgst: "", cgst: "", igst: "", state: "" });
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        textColor="primary"
        indicatorColor="primary"
        aria-label="GST Configuration Tabs"
      >
        <Tab label="GST Configuration" />
      </Tabs>

      {tabValue === 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            GST Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SGST (%)"
                type="number"
                name="sgst"
                value={form.sgst}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="CGST (%)"
                type="number"
                name="cgst"
                value={form.cgst}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="IGST (%)"
                type="number"
                name="igst"
                value={form.igst}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Select State"
                name="state"
                value={form.state}
                onChange={handleChange}
                fullWidth
              >
                {states.map((state, index) => (
                  <MenuItem key={index} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button variant="outlined" sx={{ mr: 2 }} onClick={handleReset}>
              Reset
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
          </Box>

          {/* Display all GST configs */}
          {allGSTs.length > 0 && (
            <Box sx={{ mt: 4, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
              GST Configurations:
              </Typography>
              {allGSTs.map((gst, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography>
                    <strong>{gst.state}</strong>: SGST {gst.sgst}%, CGST {gst.cgst}%, IGST {gst.igst}%
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setMessage("")} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SystemConfiguration;
